/**
 * The three F&O observability tiles' honesty decisions, as a pure module.
 *
 * `domain-tiles.tsx` cannot be tested directly (it renders, and it reaches for
 * icon values), so the part that can be *wrong* lives here instead: what number
 * a payload yields, what tone it earns, what the caption says, and — most of all
 * — when there is no number at all. Every rule below is one that has a
 * flattering failure mode if it slips.
 *
 * Three tiles, one rule each that matters most:
 *
 * 1. **Slow interactions (24 h)** — the split across application, network,
 *    endpoint and unattributed. `UNATTRIBUTED` is a *coverage gap*, not a
 *    verdict: those users' network path is invisible because DKTAA and CZPRI do
 *    not break out through Cato, so it is never counted as a clean layer and
 *    never toned as one. A measured `0` is a real finding and stays green; an
 *    unreadable workspace is dark.
 * 2. **Browser coverage** — browsers reporting ÷ active F&O users. "Nobody has
 *    installed the extension" must not render as `0 %` of a working source, and
 *    "the activity query did not answer" must not render as "nobody installed
 *    it". Four states, and only one of them is a percentage.
 * 3. **Open reports** — a 403 (not on the investigator list) and an unreadable
 *    store are both *unknown*, never a quiet week. Only `available: true` yields
 *    a count.
 *
 * The module imports types only, so `node --test` strips them and runs it as-is
 * (`tests/fo-tile-format.test.mjs`). Icons are named by a small discriminant the
 * caller maps, because an icon is a value and would break that.
 */

import type { StatisticTone } from "@norican/react";

import type { RumRolloutState } from "@/features/dynamics/console-format";
import type { FoConsoleReportList, FoConsoleRumStatus, FoConsoleSlowSummary } from "@/lib/api";

/** What a tile shows where a number would be, when there is no number. */
export const EM_DASH = "—";

/**
 * Which glyph a dark tile wears, named rather than imported.
 *
 * `domain-tiles.tsx` owns the one map from these four names to `@norican/icons`
 * values. Keeping the name here is what lets this module stay importable by a
 * type-stripped test.
 */
export type DarkIcon =
  /** A human has to inject or assign something. */
  | "credential"
  /** Nothing has been collected yet; nobody has to do anything. */
  | "pending"
  /** A read failed or a service answered badly. */
  | "unreadable"
  /** The reader is not permitted to see it. */
  | "forbidden";

/** One resolved tile, with no React and no icon values in it. */
export interface FoTileFacts {
  /** The figure, or {@link EM_DASH} when nothing was measured. */
  value: string;
  tone: StatisticTone;
  caption: string;
  /**
   * Set only when the tile carries no measurement. Its presence — not its tone
   * — is what recesses the card and suppresses the number, so it must never be
   * set alongside a real figure.
   */
  dark?: { badge: string; icon: DarkIcon };
}

function dark(
  badge: string,
  icon: DarkIcon,
  caption: string,
  tone: StatisticTone = "neutral",
): FoTileFacts {
  return { value: EM_DASH, tone, caption, dark: { badge, icon } };
}

/** Thousands-separated, or an em dash for a count that was never taken. */
export function formatTileCount(value: number | null | undefined): string {
  return value == null ? EM_DASH : value.toLocaleString();
}

/** One decimal place with a `%`, or an em dash. Never "0%" for an unknown. */
export function formatTilePercent(value: number | null | undefined): string {
  return value == null || !Number.isFinite(value) ? EM_DASH : `${value.toFixed(1)}%`;
}

/* ------------------------------------------------------------------------ *
 * 1. Slow interactions (24 h), by the layer that caused them
 * ------------------------------------------------------------------------ */

/** The four families the console folds its classification strings into. */
export const SLOW_FAMILIES = ["APPLICATION", "NETWORK", "ENDPOINT", "UNATTRIBUTED"] as const;

export type SlowFamily = (typeof SLOW_FAMILIES)[number];

/**
 * Fold a backend classification string to its family by prefix.
 *
 * Prefix-matched rather than enumerated, because the backend adds
 * classifications without a frontend release (§8: no enum mirrors live data).
 * Anything unrecognised counts as `UNATTRIBUTED` — the honest bucket, since an
 * unclassified row is precisely one whose layer is not known.
 */
export function slowFamily(classification: string | null | undefined): SlowFamily {
  const head = (classification ?? "").toUpperCase();
  for (const family of SLOW_FAMILIES) {
    if (head.startsWith(family)) {
      return family;
    }
  }
  return "UNATTRIBUTED";
}

/** Counts per family over a payload's `shares`, families with none omitted. */
export function slowSplit(
  shares: readonly { classification: string; count: number }[],
): Partial<Record<SlowFamily, number>> {
  const split: Partial<Record<SlowFamily, number>> = {};
  for (const share of shares) {
    const family = slowFamily(share.classification);
    split[family] = (split[family] ?? 0) + share.count;
  }
  return split;
}

/**
 * The caption: the split in fixed family order, then the coverage.
 *
 * Fixed order so a lookback change cannot reshuffle it, and the unattributed
 * share is named as a gap rather than listed as a peer of the three real layers.
 */
export function slowSplitCaption(
  split: Partial<Record<SlowFamily, number>>,
  coverageText: string,
): string {
  const parts = SLOW_FAMILIES.filter((family) => (split[family] ?? 0) > 0).map((family) =>
    family === "UNATTRIBUTED"
      ? `${formatTileCount(split[family])} unattributed`
      : `${formatTileCount(split[family])} ${family.toLowerCase()}`,
  );
  const splitText = parts.length > 0 ? parts.join(" · ") : "no layer attributed";
  return `${splitText} · network coverage ${coverageText}`;
}

/**
 * What a non-`ok` `access_state` yields: a hand-back, not a tile.
 *
 * The Dynamics board already owns the mapping from its six access states to a
 * badge, a tone, an icon and a sentence (`ACCESS_COPY`), so this module refuses
 * to have a second opinion — it says "this is an access-state tile" and the
 * caller renders the board's own copy. Anything else and the overview could
 * disagree with the board it links to about why the console is dark.
 */
export const ACCESS_STATE_TILE = "access-state" as const;

/**
 * The slow-interactions tile, or {@link ACCESS_STATE_TILE} when the workspace
 * could not be read.
 */
export function slowInteractionsTile(
  data: Pick<FoConsoleSlowSummary, "access_state" | "total" | "shares" | "coverage" | "errors">,
  options: {
    /** `formatCoverage(data.coverage)` from the console's formatter. */
    coverageText: string;
  },
): FoTileFacts | typeof ACCESS_STATE_TILE {
  if (data.access_state !== "ok") {
    return ACCESS_STATE_TILE;
  }

  const split = slowSplit(data.shares);
  // A truncated query makes the count a floor, not a census (§8).
  const partial = data.errors.length > 0 ? " · snapshot incomplete" : "";

  // `total` is null when the board read `ok` but the counting query itself did
  // not answer. That is the trap this tile is most likely to fall into, because
  // `null === 0` is false but `!total` is true — and rendering it as a green
  // zero would report a calm day that nobody measured.
  if (data.total == null) {
    return dark(
      "Not counted",
      "unreadable",
      `The workspace answered but the slow-interaction count did not — unknown, not zero${partial}`,
    );
  }

  if (data.total === 0) {
    return {
      value: "0",
      // A measured zero is a real finding and may go green — this is the one
      // place a zero is allowed to look good, because the workspace answered.
      tone: "success",
      caption: `Nothing was slow by the §5 rule in 24 h${partial}`,
    };
  }

  return {
    value: formatTileCount(data.total),
    tone: "warning",
    caption: `${slowSplitCaption(split, options.coverageText)}${partial}`,
  };
}

/* ------------------------------------------------------------------------ *
 * 2. Browser coverage — browsers reporting ÷ active F&O users
 * ------------------------------------------------------------------------ */

/**
 * The browser-coverage tile.
 *
 * The rollout state comes from the console's own `rumRolloutState`, so the tile
 * and the experience board's Data sources panel cannot disagree about which of
 * the four states the estate is in.
 */
export function browserCoverageTile(
  status: Pick<FoConsoleRumStatus, "users_24h" | "coverage" | "detail"> | null | undefined,
  state: RumRolloutState,
): FoTileFacts {
  if (status == null) {
    return dark("Not read", "pending", "The browser-telemetry status has not been read yet");
  }

  switch (state) {
    case "unconfigured":
      return dark(
        "Intake not configured",
        "credential",
        "NOVA has no BrowserRUM stream, so no browser can report",
        "warning",
      );
    case "disabled":
      return dark(
        "Intake disabled",
        "credential",
        "The RUM intake is switched off in NOVA's settings",
        "warning",
      );
    case "unknown":
      // The activity query did not answer. Nothing may be claimed about the
      // fleet — not even that nobody posts.
      return dark(
        "Denominator unknown",
        "unreadable",
        status.detail ||
          "The workspace did not answer the activity query, so the ratio has no denominator — unknown, not zero",
      );
    case "not_rolled_out":
      // The one that matters today. Never a 0 % meter of a working source.
      return dark(
        "Not rolled out",
        "pending",
        "No browser has posted in 24 h — the extension is still distributed manually",
      );
    case "reporting":
      break;
  }

  const pct = status.coverage?.pct ?? null;
  const withRum = status.coverage?.with_rum ?? null;
  const total = status.coverage?.total ?? null;

  if (pct == null || total == null || total === 0) {
    // Browsers post, but the active-user count is missing: a numerator with no
    // denominator is not a percentage, and must not be shown as one.
    return dark(
      "No denominator",
      "unreadable",
      `${formatTileCount(status.users_24h)} browsers posted, but the active-F&O-user count is unknown — no ratio`,
    );
  }

  return {
    value: formatTilePercent(pct),
    // The coverage scale the console already uses, so the tile's colour and the
    // meter on `/experience` agree.
    tone: pct >= 75 ? "success" : pct >= 40 ? "warning" : "danger",
    caption: `${formatTileCount(withRum)} of ${formatTileCount(total)} active F&O users have a browser reporting`,
  };
}

/* ------------------------------------------------------------------------ *
 * 3. Open reports
 * ------------------------------------------------------------------------ */

/**
 * The open-reports tile.
 *
 * Both refusals are *unknown*: a 403 means the queue exists and this sign-in may
 * not read it, an unreadable store means nobody knows how long it is. Neither is
 * a quiet week, and a `0` in either case would be the flattering reading.
 */
export function openReportsTile(
  list: Pick<FoConsoleReportList, "available" | "detail" | "counts"> | null | undefined,
  httpStatus: number | null,
  windowCaption: string,
): FoTileFacts {
  if (httpStatus === 403) {
    return dark(
      "Investigators only",
      "forbidden",
      "Your sign-in is not on the investigator list, so the queue was not read — unknown, not zero",
    );
  }
  if (list == null) {
    return dark(
      "Not read",
      httpStatus == null ? "pending" : "unreadable",
      httpStatus == null
        ? "The report queue has not been read yet"
        : `The NOVA API answered ${httpStatus}, so the queue length is unknown — not zero`,
    );
  }
  if (!list.available) {
    return dark(
      "Store unreadable",
      "unreadable",
      list.detail || "The report store could not be read, so the queue length is unknown",
    );
  }

  const open = list.counts.new ?? 0;
  const triaged = list.counts.triaged ?? 0;
  return {
    value: formatTileCount(open),
    tone: open > 0 ? "warning" : "neutral",
    caption:
      open === 0
        ? `Nobody reported an app as slow in ${windowCaption}`
        : `${formatTileCount(triaged)} already in triage · ${windowCaption}`,
  };
}
